import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppResources } from '@app/app.resources';
import { BaseForm } from '@app/modules/forms/base.form';
import { CustomRolesIndexTable, DefaultRolesIndexTable } from '@app/modules/settings/services';
import { SettingsMenuService } from '@app/modules/settings/services/settings-menu.service';
import { NotifyService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { getHelpCentreArticle } from '@app/types/translatable.type';
import { IndexView } from '@common/views';
import { Role } from '@models/account/role.model';

@Component({
    templateUrl: './role-index.view.html',
    styleUrls: ['./role-index.style.scss'],
})
export class RoleIndexView extends IndexView implements OnInit {
    isLoading = false;

    isRoleDialogOpen = false;

    roles: Role[] = [];

    learnMoreUrl = getHelpCentreArticle('4410044007831');

    constructor(
        public auth: AuthService,
        private router: Router,
        private route: ActivatedRoute,
        private notify: NotifyService,
        private http: HttpClient,
        public defaultRolesIndexTable: DefaultRolesIndexTable,
        public customRolesIndexTable: CustomRolesIndexTable,
        public settingsMenuService: SettingsMenuService
    ) {
        super();

        this.defaultRolesIndexTable.reload();
        this.customRolesIndexTable.reload();
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;
        try {
            const [roles] = await Role.where('company_id', this.auth.company.id).where('isImmutable', 1).all();

            this.roles = roles;
        } catch (error) {
            this.notify.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    closeRoleDialog(): void {
        this.isRoleDialogOpen = false;
    }

    openRoleDialog(): void {
        this.isRoleDialogOpen = true;
    }

    onCreate(role: Role, form: BaseForm): void {
        if (form.form.invalid) {
            return;
        }

        this.closeRoleDialog();

        role.isImmutable = false;

        // Name and Description must be valid, otherwise will not proceed
        if (role.name === undefined) {
            const nameErr = { message: 'settings.roles.nameFailedMessage' };
            this.onRoleCreateError(nameErr);
            return;
        }

        if (role.description === undefined) {
            const descriptionErr = { message: 'settings.roles.descriptionFailedMessage' };
            this.onRoleCreateError(descriptionErr);
            return;
        }

        // If cloning role
        if (role.cloneRole) {
            this.onClone(role);
            return;
        }

        // If role from scratch
        role.save()
            .then((res) => {
                form.reset();
                this.onRoleCreateSuccess(res);
            })
            .catch((err) => {
                this.onRoleCreateError(err);
            });
    }

    /**
     * When new role creation involves cloning a role
     * @param {Role} role [description]
     */
    private onClone(role: Role): void {
        const roleToClone = this.roles.find(
            (existingRoles) => existingRoles.name.toLowerCase() === role.cloneRole.toLowerCase()
        );

        this.http
            .post(AppResources.DuplicateRole.replace(':roleId', roleToClone.id.toString()), {})
            .toPromise()
            .then((res: Response) => {
                const id = res['data'].id;
                const newRole = new Role({
                    id: id,
                    name: role.name,
                    description: role.description,
                });
                newRole
                    .save()
                    .then((res) => this.onRoleCreateSuccess(res))
                    .catch((err) => this.onRoleCreateError(err));
            })
            .catch((err) => {
                this.onRoleCreateError(err);
            });
    }

    private onRoleCreateSuccess(role: Role): void {
        this.router.navigate([role.id, 'permissions'], { relativeTo: this.route });
    }

    private onRoleCreateError(err: any): void {
        this.notify.error(err.message || 'settings.roles.createRoleFailedMessage');
        this.isLoading = false;
    }
}
